import { useRef, useEffect, useMemo } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  IconExpand,
  Table,
  useQueryState,
  useGetList,
  useScroll,
  useFilters,
  usePrevious,
  IconLoader,
  PermissionEnum,
  PermissionDenied,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  incorporationDatesListColumns,
  incorporationDatesListColumnsLabels,
  incorporationDatesListFiltersSchema,
  incorporationDatesListSchema,
} from '../variables/incorporation-dates';
import { useLazyGetIncorporationDatesQuery } from '../store/services';
import { RiskScoreItem, IncorporationDateListItem } from '../../types';
import {
  useLazyGetAmlDictionariesQuery,
  useLazyGetAmlBaseCurrenciesQuery,
  useLazyGetAmlCurrenciesQuery,
} from '../../../store/dictionaries/services';
import ROUTES from '../../../routes';

import cn from '../corporate-clients-risk.module.css';

const formatIncorporationDatesList = (
  list: IncorporationDateListItem[],
): IncorporationDateListItem[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      riskCategory: (
        <div>{`${item.riskCategory.charAt(0)}${item.riskCategory
          .slice(1)
          .toLowerCase()} risk`}</div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const IncorporationDateList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    corporateClientsRisk: {
      incorporationDates: {
        list: { values, pagination, errorsList },
      },
    },
    amlDictionaries: {
      activePermissions,
      riskScoreRanges,
      currencies,
      baseCurrency,
    },
  } = useAppSelector((state) => state);

  const [getDictionaries] = useLazyGetAmlDictionariesQuery();
  const [getAmlBaseCurrency] = useLazyGetAmlBaseCurrenciesQuery();
  const [getAmlCurrencies] = useLazyGetAmlCurrenciesQuery();

  const { onSortChange, filters, onFilterChange } = useQueryState(
    incorporationDatesListSchema,
  );

  const { search } = useLocation();

  const prevFilters = usePrevious(filters);

  const formattedData = useMemo(() => {
    const data = formatIncorporationDatesList(
      values as IncorporationDateListItem[],
    );

    return data;
  }, [values]);

  const [getIncorporationDates, { isLoading, isFetching }] =
    useLazyGetIncorporationDatesQuery();

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<RiskScoreItem>({
      getList: getIncorporationDates,
      onFilterChange,
      onSortChange,
      schema: incorporationDatesListSchema,
    });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: incorporationDatesListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !values &&
      activePermissions?.includes(
        PermissionEnum.SETTINGS_INCORPORATION_DATES_LIST,
      )
    ) {
      getListAction({
        filters,
      });
    }
  }, [filters, prevFilters, values, activePermissions]);

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.SETTINGS_INCORPORATION_DATES_LIST,
      )
    ) {
      if (!riskScoreRanges) {
        getDictionaries();
      }

      if (!currencies) {
        getAmlCurrencies();
      }

      if (!baseCurrency) {
        getAmlBaseCurrency();
      }
    }
  }, [activePermissions, riskScoreRanges, currencies, baseCurrency]);

  useEffect(() => {
    let title = 'Date of Incorporation';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;
  let globalContent = (
    <div className={cn.incorporationDatesLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (
      activePermissions?.includes(
        PermissionEnum.SETTINGS_INCORPORATION_DATES_LIST,
      )
    ) {
      if (isLoading) {
        content = (
          <div className={cn.incorporationDatesLoader}>
            <IconLoader />
          </div>
        );
      } else {
        content = (
          <Table
            columns={incorporationDatesListColumns}
            data={formattedData || []}
            onSort={() => {}}
            sort={{}}
            filters={filters}
            columnsLabels={incorporationDatesListColumnsLabels}
            onFilterChange={(filtersValues) => onFilter(filtersValues)}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isFetching}
            take="30"
            setPage={(data) => setPage({ ...data, filters })}
            pagination={pagination}
            tableFormClassName={cn.sicCodesTableForm}
            error={errorsList || errorList}
            clickable
          />
        );
      }

      globalContent = (
        <div className={cn.incorporationDates}>
          <div className={cn.incorporationDatesTop}>
            <div className={cn.incorporationDatesControls}>
              {activePermissions?.includes(
                PermissionEnum.SETTINGS_INCORPORATION_DATES_CREATE_ACTION,
              ) && (
                <NavLink
                  to={`${
                    ROUTES.CORPORATE_CLIENTS_RISK_DATE_OF_INCORPORATION_CREATE
                  }${search || ''}`}
                  className={cn.incorporationDatesLink}
                >
                  <IconExpand />
                  Add range of years
                </NavLink>
              )}
            </div>
          </div>
          <div className={cn.incorporationDatesTableWrapper} ref={scrollRef}>
            {content}
          </div>
          <Outlet />
        </div>
      );
    } else {
      globalContent = <PermissionDenied />;
    }
  }

  return globalContent;
};

export default IncorporationDateList;
